import type { OpeningLayoutBounds } from "building-types/framing/framingLayoutTypes";
import type { FramingProfileDescriptor } from "building-types/framing/framingProfileDescriptorTypes";
import type {
  BlockingOpeningGap,
  GeneratedWallBlockingMember,
  WallBlockingElevationOptions,
  WallBlockingGenerationContext,
} from "building-types/framing/wallFramingBlockingTypes";
import type { WallOpeningHorizontalBand } from "building-types/framing/wallFramingOpeningClusterTypes";
import type {
  BandFramingOptions,
  OpeningFramingConfigResolver,
} from "building-types/framing/wallFramingOpeningConfigTypes";
import { WallFramingType } from "building-types/framing/wallFramingTypes";
import type { WallBlockingHeightMode, WallFramingMetadata } from "building-types/walls/wallFramingMetaDataTypes";
import { DIST_EPSILON } from "geom/core";
import { polyLine2ByTwoPoints } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { Vec2 } from "geom/vec2";
import { computeProjectedDistance, layoutPointFromPerpParallel } from "../framingLayoutUtils";
import {
  collectBandOpeningFramingConfigs,
  createBandJackKingResolution,
  mergeBandJackKingFramingConfig,
  resolveOpeningFramingConfigForBand,
} from "./wallFramingOpeningConfig";
import { configCount } from "./wallFramingOpenings";

/** Matches legacy {@link packages/building-generation/elements/walls/wallFraming.ts}. */
export const BLOCKING_MIN_DISTANCE = 1.5;

/** Default blocking elevations above wall bottom parallel (legacy 42" / 54"). */
export const BLOCKING_PARALLEL_ELEVATION_LOW = 42;
export const BLOCKING_PARALLEL_ELEVATION_HIGH = 54;

/** Common studs on the spacing grid use a centerline offset (see createStudSegments). */
function commonStudLeadingFacePerp(centerPerp: number, spacingSign: 1 | -1, studHalfThickness: number): number {
  return centerPerp - spacingSign * studHalfThickness;
}

function commonStudTrailingFacePerp(centerPerp: number, spacingSign: 1 | -1, studHalfThickness: number): number {
  return centerPerp + spacingSign * studHalfThickness;
}

/** Perimeter end studs sit flush on the edge without centerline offset. */
function perimeterEndStudTrailingFacePerp(ctx: WallBlockingGenerationContext, atMinPerpEnd: boolean): number {
  if (atMinPerpEnd) {
    return ctx.spacingSign > 0 ? ctx.minPerpDist + ctx.studThickness : ctx.minPerpDist;
  }
  return ctx.spacingSign > 0 ? ctx.maxPerpDist : ctx.maxPerpDist - ctx.studThickness;
}

function makeBlockingRail(
  ctx: WallBlockingGenerationContext,
  startPerp: number,
  endPerp: number,
  parallelDistance: number,
): PolyLine2 {
  const [x0, y0] = layoutPointFromPerpParallel(
    startPerp,
    parallelDistance,
    ctx.planeOrigin,
    ctx.perpDirection,
    ctx.framingDirection,
  );
  const [x1, y1] = layoutPointFromPerpParallel(
    endPerp,
    parallelDistance,
    ctx.planeOrigin,
    ctx.perpDirection,
    ctx.framingDirection,
  );
  return polyLine2ByTwoPoints(x0, y0, x1, y1);
}

function blockingSpanLength(startPerp: number, endPerp: number): number {
  return Math.abs(endPerp - startPerp);
}

function addBlockingMember(
  members: GeneratedWallBlockingMember[],
  ctx: WallBlockingGenerationContext,
  startPerp: number,
  endPerp: number,
  parallelElevation: number,
  profileDescriptor: FramingProfileDescriptor,
): boolean {
  if (blockingSpanLength(startPerp, endPerp) < BLOCKING_MIN_DISTANCE) {
    return false;
  }
  const parallelDistance = ctx.wallBottomParallel + parallelElevation;
  members.push({
    polyline: makeBlockingRail(ctx, startPerp, endPerp, parallelDistance),
    profileDescriptor,
    framingType: WallFramingType.BLOCKING,
  });
  return true;
}

function nextBlockingElevation(elevationOptions: WallBlockingElevationOptions, prevElevation: number): number {
  if (elevationOptions.heightMode === "fixed") {
    return elevationOptions.fixedHeightInches;
  }
  return prevElevation === elevationOptions.alternateHighHeightInches
    ? elevationOptions.alternateLowHeightInches
    : elevationOptions.alternateHighHeightInches;
}

function bayOverlapsGap(bayStartPerp: number, bayEndPerp: number, gap: BlockingOpeningGap): boolean {
  const bayMin = Math.min(bayStartPerp, bayEndPerp);
  const bayMax = Math.max(bayStartPerp, bayEndPerp);
  return bayMin < gap.extendedMaxPerp + DIST_EPSILON && bayMax > gap.extendedMinPerp - DIST_EPSILON;
}

function findGapForBay(
  bayStartPerp: number,
  bayEndPerp: number,
  gaps: readonly BlockingOpeningGap[],
): BlockingOpeningGap | undefined {
  for (const gap of gaps) {
    if (bayOverlapsGap(bayStartPerp, bayEndPerp, gap)) {
      return gap;
    }
  }
  return undefined;
}

function blockingEndBeforeOpening(gap: BlockingOpeningGap, spacingSign: 1 | -1, studThickness: number): number {
  if (spacingSign > 0) {
    return gap.roMinPerp - gap.jackOffset - studThickness;
  }
  return gap.roMaxPerp + gap.jackOffset + studThickness;
}

function blockingResumeAfterOpening(gap: BlockingOpeningGap, spacingSign: 1 | -1, studThickness: number): number {
  if (spacingSign > 0) {
    return gap.roMaxPerp + gap.jackOffset + studThickness;
  }
  return gap.roMinPerp - gap.jackOffset - studThickness;
}

/**
 * Spacing-grid centerlines for common studs only (index 1+), matching
 * {@link generateFramingSpacingOriginPoints} — the first common stud is one spacing from layout start.
 */
function buildCommonStudCenterPerps(ctx: WallBlockingGenerationContext): number[] {
  const bays: number[] = [];
  let spacingIndex = 1;
  for (;;) {
    const perp = ctx.layoutStartPerp + ctx.spacingSign * spacingIndex * ctx.spacing;
    spacingIndex += 1;
    if (ctx.spacingSign > 0 && perp > ctx.maxPerpDist + DIST_EPSILON) {
      break;
    }
    if (ctx.spacingSign < 0 && perp < ctx.minPerpDist - DIST_EPSILON) {
      break;
    }
    if (perp >= ctx.minPerpDist - DIST_EPSILON && perp <= ctx.maxPerpDist + DIST_EPSILON) {
      bays.push(perp);
    }
  }
  return bays;
}

export function resolveWallBlockingElevationOptions(
  framingMetadata: Pick<
    WallFramingMetadata,
    | "blockingHeightMode"
    | "blockingFixedHeightInches"
    | "blockingAlternateLowHeightInches"
    | "blockingAlternateHighHeightInches"
  >,
): WallBlockingElevationOptions {
  const heightMode: WallBlockingHeightMode = framingMetadata.blockingHeightMode ?? "alternate";
  return {
    heightMode,
    fixedHeightInches: framingMetadata.blockingFixedHeightInches ?? BLOCKING_PARALLEL_ELEVATION_LOW,
    alternateLowHeightInches: framingMetadata.blockingAlternateLowHeightInches ?? BLOCKING_PARALLEL_ELEVATION_LOW,
    alternateHighHeightInches: framingMetadata.blockingAlternateHighHeightInches ?? BLOCKING_PARALLEL_ELEVATION_HIGH,
  };
}

export function buildBlockingOpeningGaps(
  openingBands: readonly WallOpeningHorizontalBand[],
  resolveOpeningConfig: OpeningFramingConfigResolver,
  bandFramingOptions: BandFramingOptions,
  studThickness: number,
): BlockingOpeningGap[] {
  const gaps: BlockingOpeningGap[] = [];
  for (const band of openingBands) {
    const jackKingResolution = createBandJackKingResolution(band.openings, resolveOpeningConfig, bandFramingOptions);
    const config =
      jackKingResolution.merged ??
      mergeBandJackKingFramingConfig(collectBandOpeningFramingConfigs(band.openings, resolveOpeningConfig));
    const jackOffset = config ? configCount(config.jackStudConfig) * studThickness : 0;
    let roMinPerp = Infinity;
    let roMaxPerp = -Infinity;
    for (const opening of band.openings) {
      roMinPerp = Math.min(roMinPerp, opening.bounds.startPerp, opening.bounds.endPerp);
      roMaxPerp = Math.max(roMaxPerp, opening.bounds.startPerp, opening.bounds.endPerp);
    }
    gaps.push({
      extendedMinPerp: band.combinedPerpExtent.startPerp,
      extendedMaxPerp: band.combinedPerpExtent.endPerp,
      roMinPerp,
      roMaxPerp,
      jackOffset,
    });
  }
  return gaps.sort((a, b) => a.extendedMinPerp - b.extendedMinPerp);
}

export function buildBlockingOpeningGapsFromBounds(
  openingBoundsList: readonly OpeningLayoutBounds[],
  resolveOpeningConfig: OpeningFramingConfigResolver,
  studThickness: number,
): BlockingOpeningGap[] {
  const gaps: BlockingOpeningGap[] = [];
  for (const bounds of openingBoundsList) {
    const config = resolveOpeningFramingConfigForBand(bounds.openingIndex, resolveOpeningConfig, {
      enabled: false,
      merged: undefined,
    });
    const jackOffset = config ? configCount(config.jackStudConfig) * studThickness : 0;
    const kingOffset = config ? configCount(config.kingStudConfig) * studThickness : studThickness;
    const roMinPerp = Math.min(bounds.startPerp, bounds.endPerp);
    const roMaxPerp = Math.max(bounds.startPerp, bounds.endPerp);
    gaps.push({
      extendedMinPerp: roMinPerp - jackOffset - kingOffset,
      extendedMaxPerp: roMaxPerp + jackOffset + kingOffset,
      roMinPerp,
      roMaxPerp,
      jackOffset,
    });
  }
  return gaps.sort((a, b) => a.extendedMinPerp - b.extendedMinPerp);
}

/**
 * Fire blocking between stud faces along the wall run (legacy frameWall stud loop).
 */
export function generateWallBlockingMembers(
  ctx: WallBlockingGenerationContext,
  profileDescriptor: FramingProfileDescriptor,
  openingGaps: readonly BlockingOpeningGap[],
  elevationOptions: WallBlockingElevationOptions,
): GeneratedWallBlockingMember[] {
  const members: GeneratedWallBlockingMember[] = [];
  const lastStudLeadingPerp =
    ctx.spacingSign > 0 ? ctx.maxPerpDist - ctx.studThickness : ctx.minPerpDist + ctx.studThickness;

  const blockingRunsTowardMaxPerp = ctx.spacingSign > 0;
  let prevStudEndPerp = perimeterEndStudTrailingFacePerp(ctx, blockingRunsTowardMaxPerp);
  let prevBlockingElevation = elevationOptions.alternateLowHeightInches;

  const commonStudCenters = buildCommonStudCenterPerps(ctx);

  for (const bayCenterPerp of commonStudCenters) {
    const studTrailingPerp = commonStudTrailingFacePerp(bayCenterPerp, ctx.spacingSign, ctx.studHalfThickness);

    if (ctx.spacingSign > 0 && studTrailingPerp >= lastStudLeadingPerp - DIST_EPSILON) {
      continue;
    }
    if (ctx.spacingSign < 0 && studTrailingPerp <= lastStudLeadingPerp + DIST_EPSILON) {
      continue;
    }

    const gap = findGapForBay(bayCenterPerp, bayCenterPerp + ctx.spacingSign * ctx.studThickness, openingGaps);
    const blockingElevation = nextBlockingElevation(elevationOptions, prevBlockingElevation);

    const blockingStartPerp = prevStudEndPerp;
    let blockingEndPerp = commonStudLeadingFacePerp(bayCenterPerp, ctx.spacingSign, ctx.studHalfThickness);

    if (gap) {
      const resumePerp = blockingResumeAfterOpening(gap, ctx.spacingSign, ctx.studThickness);
      if (Math.abs(resumePerp - prevStudEndPerp) > DIST_EPSILON) {
        blockingEndPerp = blockingEndBeforeOpening(gap, ctx.spacingSign, ctx.studThickness);
        if (addBlockingMember(members, ctx, blockingStartPerp, blockingEndPerp, blockingElevation, profileDescriptor)) {
          prevBlockingElevation = blockingElevation;
        }
        prevStudEndPerp = resumePerp;
      }
    } else {
      if (addBlockingMember(members, ctx, blockingStartPerp, blockingEndPerp, blockingElevation, profileDescriptor)) {
        prevBlockingElevation = blockingElevation;
      }
      prevStudEndPerp = studTrailingPerp;
    }
  }

  const finalElevation = nextBlockingElevation(elevationOptions, prevBlockingElevation);
  addBlockingMember(members, ctx, prevStudEndPerp, lastStudLeadingPerp, finalElevation, profileDescriptor);

  return members;
}

export function createWallBlockingGenerationContext(
  planeOrigin: Vec2,
  perpDirection: Vec2,
  framingDirection: Vec2,
  wallBottomParallel: number,
  studThickness: number,
  spacing: number,
  spacingSign: 1 | -1,
  layoutStartPerp: number,
  minPerpDist: number,
  maxPerpDist: number,
): WallBlockingGenerationContext {
  return {
    planeOrigin,
    perpDirection,
    framingDirection,
    wallBottomParallel,
    studThickness,
    studHalfThickness: studThickness / 2,
    spacing,
    spacingSign,
    layoutStartPerp,
    minPerpDist,
    maxPerpDist,
  };
}

export function layoutStartPerpFromOrigin(planeOrigin: Vec2, perpDirection: Vec2, layoutStartPt: Vec2): number {
  return computeProjectedDistance(layoutStartPt, planeOrigin, perpDirection);
}
